import type { ID } from "@/lib/catalog/types";
import { api } from "@/lib/apiClient";

export interface PermissionCategoryRecord {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
}

export type PermissionCategoryDraft = Pick<PermissionCategoryRecord, "name">;

const LS_KEY = "inventory.permissionCategories.v1";

type CategoryApiRecord = {
  id?: number | string;
  name: string;
  created_at?: string | null;
  updated_at?: string | null;
};

type UpsertApiResponse = {
  message?: string;
  data?: CategoryApiRecord;
};

type DeleteApiResponse = {
  message?: string;
};

type UpsertResult = {
  record: PermissionCategoryRecord;
  message: string;
};

type DeleteResult = {
  success: boolean;
  message: string;
};

function dedupeById(items: PermissionCategoryRecord[]): PermissionCategoryRecord[] {
  const byId = new Map<ID, PermissionCategoryRecord>();
  for (const item of items) byId.set(item.id, item);
  return Array.from(byId.values());
}

function load(): PermissionCategoryRecord[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as PermissionCategoryRecord[];
    return Array.isArray(parsed) ? dedupeById(parsed) : [];
  } catch {
    return [];
  }
}

function save(items: PermissionCategoryRecord[]) {
  localStorage.setItem(LS_KEY, JSON.stringify(dedupeById(items)));
}

function extractRowsFromResponse(body: unknown): CategoryApiRecord[] {
  if (Array.isArray(body)) return body as CategoryApiRecord[];
  if (!body || typeof body !== "object") return [];
  const root = body as Record<string, unknown>;
  const data = root.data;
  if (Array.isArray(data)) return data as CategoryApiRecord[];
  if (data && typeof data === "object" && !Array.isArray(data)) {
    const inner = (data as Record<string, unknown>).data;
    if (Array.isArray(inner)) return inner as CategoryApiRecord[];
  }
  return [];
}

function readLastPageFromListBody(body: unknown): number | null {
  if (!body || typeof body !== "object") return null;
  const meta = (body as Record<string, unknown>).meta;
  if (meta && typeof meta === "object" && !Array.isArray(meta)) {
    const lp = (meta as Record<string, unknown>).last_page;
    if (typeof lp === "number" && lp >= 1) return lp;
  }
  return null;
}

function extractFromUpsertBody(body: unknown): CategoryApiRecord | null {
  if (!body || typeof body !== "object") return null;
  const root = body as Record<string, unknown>;
  const data = root.data;
  if (data && typeof data === "object" && !Array.isArray(data)) {
    return data as CategoryApiRecord;
  }
  return null;
}

function mapApiRecord(record: CategoryApiRecord): PermissionCategoryRecord {
  const created = record.created_at || new Date().toISOString();
  const updated = record.updated_at || created;
  return {
    id: String(record.id ?? ""),
    name: record.name,
    createdAt: created,
    updatedAt: updated,
  };
}

export function listPermissionCategories(): PermissionCategoryRecord[] {
  return load().sort((a, b) => a.name.localeCompare(b.name));
}

const PER_PAGE = 50;

export async function syncPermissionCategoriesFromApi() {
  const collected: CategoryApiRecord[] = [];
  let page = 1;
  let lastPage = 1;
  do {
    const response = await api.get("/api/v1/permission-categories", {
      params: { page, per_page: PER_PAGE },
    });
    const body = response.data;
    collected.push(...extractRowsFromResponse(body));
    lastPage = readLastPageFromListBody(body) ?? 1;
    page += 1;
  } while (page <= lastPage && page <= 50);

  const records = dedupeById(collected.map(mapApiRecord).filter((r) => Boolean(r.id)));
  save(records);
  return listPermissionCategories();
}

export async function createPermissionCategory(draft: PermissionCategoryDraft): Promise<UpsertResult> {
  const payload = { name: draft.name.trim() };
  const response = await api.post<UpsertApiResponse>("/api/v1/permission-categories", payload);
  const created = extractFromUpsertBody(response.data);
  if (!created || created.id == null) throw new Error("Invalid create permission category response.");
  const record = mapApiRecord(created);

  const items = load();
  const idx = items.findIndex((d) => d.id === record.id);
  if (idx >= 0) items[idx] = record;
  else items.unshift(record);
  save(items);

  return { record, message: response.data?.message || "Permission category created successfully." };
}

export async function updatePermissionCategory(id: ID, draft: PermissionCategoryDraft): Promise<UpsertResult | null> {
  const items = load();
  const idx = items.findIndex((d) => d.id === id);
  if (idx === -1) return null;

  const payload = { name: draft.name.trim() };
  const response = await api.put<UpsertApiResponse>(`/api/v1/permission-categories/${id}`, payload);
  const updated = extractFromUpsertBody(response.data);
  if (!updated) throw new Error("Invalid update permission category response.");

  const record = mapApiRecord(updated);
  items[idx] = record;
  save(items);

  return { record, message: response.data?.message || "Permission category updated successfully." };
}

export async function deletePermissionCategory(id: ID): Promise<DeleteResult> {
  const response = await api.delete<DeleteApiResponse>(`/api/v1/permission-categories/${id}`);
  const items = load();
  const next = items.filter((d) => d.id !== id);
  const removed = next.length !== items.length;
  if (removed) save(next);
  return { success: removed, message: response.data?.message || "Permission category deleted successfully." };
}
